import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Check, MapPin, User, ShieldCheck } from "lucide-react";
import {
  Select as AntSelect,
  Checkbox,
  SearchBar,
  Pagination,
  message,
  Empty,
} from "antd";
import { IoArrowBackCircleOutline, IoCheckmark } from "react-icons/io5";
import { HiOutlineArrowRight } from "react-icons/hi2";
import { getApi, postApi } from "../../../../../api/api_instance";
import { API_URLS } from "../../../../../api/apiUrls";
import { fetchFormAssignments } from "../../../../../redux/ViewForm/viewFormSlice";
import PopupCard from "../../../../Auth/PopupCard";
import CustomButton from "../../../../../components/Elements/Buttons/CustomButtons";
import TextStyles from "../../../../../components/Elements/TextStyles/TextStyles";
import Usericon from ".././../../../../assets/svgs/user-icon.png";
import Locationicon from ".././../../../../assets/svgs/location-icon.png";
import checkicon from ".././../../../../assets/svgs/check-icon.png";
import firsticon from "../../../../../assets/svgs/Checkfirst.svg";
import confirmicon from "../../../../../assets/svgs/Confirmbtn.png";
import "./AuthorizeToManage.css";

const { Option } = AntSelect;

const AuthorizeToManage = ({
  onClose,
  standalone,
  projectId: propProjectId,
  formType: propFormType,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationState = useLocation();
  const { formId } = useParams();
  const authUser = useSelector((state) => state.auth.user);
  const clientId = authUser?.client_id?._id || authUser?._id;
  const projectId = propProjectId || locationState.state?.projectId;

  // Org Mode Details
  const orgModeLocked = authUser?.orgModeLocked || false;
  const orgStructureMode = authUser?.orgStructureMode || "location-first"; // "location-first" or "department-first"

  // State
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // Locations or Departments
  const [allUsers, setAllUsers] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [alreadyAssignedIds, setAlreadyAssignedIds] = useState([]);
  const [mapping, setMapping] = useState({}); // { itemId: userId }
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "success",
    title: "",
    message: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOrgStructureWarning, setShowOrgStructureWarning] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Constants
  const isLocationFirst = orgStructureMode === "location-first";

  // Data Fetching
  const fetchData = async () => {
    if (!clientId) return;
    setLoading(true);

    let fetchedItems = [];
    let fetchedUsers = [];
    let usersFetchSuccess = true;

    try {
      // 1. Fetch Items (Locations or Departments)
      const itemsUrl = isLocationFirst
        ? API_URLS.GET_LOCATIONS_OF_CLIENT
        : API_URLS.GET_DEPARTMENT;

      const itemsResponse = await getApi({ apiUrl: itemsUrl });
      fetchedItems = itemsResponse?.data?.data || itemsResponse?.data || [];
      setItems(fetchedItems);
    } catch (error) {
      console.warn("Failed to fetch locations/departments:", error);
    }

    try {
      // 2. Fetch All Users for filtering
      const usersResponse = await getApi({
        apiUrl: API_URLS.GET_ALL_USER_BY_CLIENT_ID(clientId, 1, 1000, ""),
      });
      fetchedUsers = usersResponse?.data || [];
      setAllUsers(fetchedUsers);
    } catch (error) {
      console.warn("Failed to fetch users:", error);
      usersFetchSuccess = false;
    }

    if (
      fetchedItems.length === 0 ||
      fetchedUsers.length === 0 ||
      !usersFetchSuccess
    ) {
      setShowOrgStructureWarning(true);
    }

    try {
      // 3. Fetch Already Assigned LS1s if formId and projectId are present
      if (formId && projectId) {
        const ls1Url = API_URLS.GET_LS_ONE(formId, projectId);
        const ls1Response = await getApi({ apiUrl: ls1Url });
        const ls1Data = ls1Response?.data?.data || ls1Response?.data || [];

        const assignedIds = [];
        const newMapping = {};

        ls1Data.forEach((allotment) => {
          if (allotment.status === "active") {
            const scopeId = isLocationFirst
              ? allotment.locationId
              : allotment.departmentId;
            const employeeId = allotment.employee?._id;
            if (scopeId && employeeId) {
              assignedIds.push(scopeId);
              newMapping[scopeId] = employeeId;
            }
          }
        });

        setAlreadyAssignedIds(assignedIds);
        setSelectedItemIds((prev) => [...new Set([...prev, ...assignedIds])]);
        setMapping((prev) => ({ ...prev, ...newMapping }));
      }
    } catch (error) {
      console.error("Data fetching failed:", error);
      // We don't show the generic error popup here if it's just missing org structure
      if (
        fetchedItems.length > 0 &&
        fetchedUsers.length > 0 &&
        usersFetchSuccess
      ) {
        triggerPopup("error", "Error", "Failed to fetch organizational data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId, orgStructureMode, projectId, formId]);

  // Handlers
  const triggerPopup = (type, title, message) => {
    setPopupData({ type, title, message });
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItemIds((prev) => {
        const nonAssignedFilteredIds = filteredItems
          .map((item) => item._id)
          .filter((id) => !alreadyAssignedIds.includes(id));
        return [...new Set([...prev, ...nonAssignedFilteredIds])];
      });
    } else {
      const filteredItemIds = filteredItems.map((item) => item._id);
      setSelectedItemIds((prev) =>
        prev.filter(
          (id) =>
            alreadyAssignedIds.includes(id) || !filteredItemIds.includes(id),
        ),
      );
    }
  };

  const handleItemToggle = (id) => {
    if (alreadyAssignedIds.includes(id)) return;
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleUserMapping = (itemId, userId) => {
    setMapping((prev) => ({ ...prev, [itemId]: userId }));
  };

  const proceedToSave = async () => {
    const projectId = propProjectId || locationState.state?.projectId;

    const payload = {
      formId: formId,
      formType: propFormType || "standard",
      allotmentType: "handle",
      projectId: projectId,
      expiresAt: null, // Optional
      targets: selectedItemIds
        .filter((id) => !alreadyAssignedIds.includes(id))
        .map((id) => ({
          employeeId: mapping[id],
          locationId: isLocationFirst ? id : null,
          departmentId: !isLocationFirst ? id : null,
          sections: [], // Empty array for full access
        })),
    };

    console.log("Hierarchy Allotment Payload:", payload);

    setLoading(true);
    setShowConfirmModal(false);
    try {
      const response = await postApi({
        apiUrl: API_URLS.AUTHORIZE_HIERARCHY,
        data: payload,
      });

      if (
        response?.success ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        triggerPopup("success", "Success", "Form authorization successfully!");
        dispatch(
          fetchFormAssignments({ formId, isEmployee: false, projectId }),
        );
        if (onClose) setTimeout(onClose, 2000);
      } else {
        throw new Error(response?.message || response?.data?.message);
      }
    } catch (error) {
      console.error("Save failed:", error);
      triggerPopup(
        "error",
        "Error",
        error.message || "Failed to save hierarchy.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    const newSelections = selectedItemIds.filter(
      (id) => !alreadyAssignedIds.includes(id),
    );
    if (newSelections.length === 0) {
      triggerPopup(
        "error",
        "Selection Required",
        "Please select at least one location/department.",
      );
      return;
    }

    // Check if all selected items have a mapping
    const missingMappings = newSelections.filter((id) => !mapping[id]);
    if (missingMappings.length > 0) {
      triggerPopup(
        "error",
        "Missing Supervisors",
        "Please assign a supervisor for all selected items.",
      );
      return;
    }

    setShowConfirmModal(true);
  };

  useEffect(() => {
    // Redux effects removed because we use local async/await for hierarchy allotment
  }, [dispatch, onClose]);

  // Computations
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const name = isLocationFirst
        ? item.location_name || item.name
        : item.department_name || item.name;
      return name?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [items, searchTerm, isLocationFirst]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  const getUsersForItem = (itemId) => {
    // Filter users linked to this specific item
    return allUsers.filter((user) => {
      const linkedId = isLocationFirst ? user.locationId : user.departmentId;
      return linkedId === itemId;
    });
  };

  const selectedPairs = useMemo(() => {
    return selectedItemIds
      .filter((id) => !alreadyAssignedIds.includes(id))
      .map((id) => {
        const item = items.find((i) => i._id === id);
        const itemName = isLocationFirst
          ? item?.location_name || item?.name
          : item?.department_name || item?.name;
        const user = allUsers.find((u) => u._id === mapping[id]);
        return {
          id,
          itemName,
          userName: user?.name || "N/A",
          isAlreadyAssigned: false,
        };
      });
  }, [
    selectedItemIds,
    items,
    mapping,
    allUsers,
    isLocationFirst,
    alreadyAssignedIds,
  ]);
  const uniqueUsersCount = useMemo(() => {
    const userNames = selectedPairs
      .map((p) => p.userName)
      .filter((name) => name && name !== "N/A");
    return new Set(userNames).size;
  }, [selectedPairs]);
  // Reset page on search
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="md:p-6 lg:p-0 mb-20 sm:mb-6 md:mb-6">
      <div className="md:p-6 lg:p-0 ">
        {/* <div className="flex items-center gap-2 sm:mb-1.5 text-lg sm:text-xl font-bold flex-wrap">
          <IoArrowBackCircleOutline
            onClick={() => navigate(-1)}
            className="w-8 sm:w-10 h-8 sm:h-10 text-[#06513D] hover:text-white hover:bg-[#06513D] rounded-full transition cursor-pointer"
          />
          <div className="text-[#06513D] font-[Calibri] text-[18px] sm:text-[20px] leading-none tracking-tight">
            Authorize Form to Manage
          </div>
        </div> */}

        {/* Main Card */}
        <div className="authorize-card max-w-6xl mx-auto bg-white rounded-[20px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden mt-4">
          {/* Top Control Bar */}
          <div className="authorize-topbar p-6 flex flex-col md:flex-row justify-between items-center gap-6">
            <label className="authorize-selectall  flex gap-2 items-center text-[#06513D] text-xs font-semibold px-3 py-2 rounded-lg uppercase tracking-wide">
              <input
                type="checkbox"
                checked={(() => {
                  const selectableFilteredItems = filteredItems.filter(
                    (item) => !alreadyAssignedIds.includes(item._id),
                  );
                  return (
                    selectableFilteredItems.length > 0 &&
                    selectableFilteredItems.every((item) =>
                      selectedItemIds.includes(item._id),
                    )
                  );
                })()}
                disabled={
                  filteredItems.filter(
                    (item) => !alreadyAssignedIds.includes(item._id),
                  ).length === 0
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="accent-[#06513D] w-4 h-4"
              />
              Select All
            </label>
            <div className="w-full md:w-[400px] relative">
              <input
                type="text"
                placeholder="Search..."
                className="authorize-search w-full h-11 pl-12 pr-4 bg-white rounded-xl focus:outline-none focus:ring-1 focus:ring-[#06513D]/40 transition-all font-medium text-gray-600"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Table Header */}
          <div className="authorize-header px-10 py-3 flex items-center bg-white">
            <div className="w-[50%] ml-8">
              <span className="text-gray-900 font-bold text-lg">
                {isLocationFirst ? "Locations" : "Departments"}
              </span>
            </div>
            <div className="w-[45%] text-left">
              <span className="text-gray-900 font-bold text-lg ml-8">
                Assign Supervisor
              </span>
            </div>
          </div>

          {/* List Body */}
          <div className="px-4 md:px-10 py-8 space-y-6 min-h-[320px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                <div className="w-12 h-12 border-4 border-[#06513D] border-t-transparent rounded-full animate-spin mb-4"></div>
                <span className="text-[#06513D] font-bold">
                  Synchronizing Organizational Structure...
                </span>
              </div>
            ) : filteredItems.length > 0 ? (
              <>
                {paginatedItems.map((item) => {
                  const itemId = item._id;
                  const itemName = isLocationFirst
                    ? item.location_name || item.name
                    : item.department_name || item.name;
                  const linkedUsers = getUsersForItem(itemId);

                  return (
                    <div
                      key={itemId}
                      className="flex items-center gap-8 animate-fadeIn"
                    >
                      <div className="flex items-center justify-center w-6 h-6">
                        {alreadyAssignedIds.includes(itemId) ? (
                          <div className="scale-125 w-[16px] h-[16px] bg-[#06513D] rounded-[4px] flex items-center justify-center">
                            <IoCheckmark className="text-white text-xs font-bold" />
                          </div>
                        ) : (
                          <Checkbox
                            className="scale-125 custom-green-checkbox"
                            checked={selectedItemIds.includes(itemId)}
                            onChange={() => handleItemToggle(itemId)}
                          />
                        )}
                      </div>

                      <div className="flex-1 flex items-center justify-between gap-6">
                        {/* Name Box */}
                        <div
                          className={`authorize-item-box flex-1 bg-white h-[48px] rounded-xl flex items-center justify-between px-6 ${alreadyAssignedIds.includes(itemId) ? "opacity-75" : ""}`}
                        >
                          <span className="text-gray-600 font-medium">
                            {itemName}
                          </span>
                          {/* {alreadyAssignedIds.includes(itemId) && (
                            <span className="bg-[#E6F4EA] text-[#06513D] border border-[#06513D]/20 px-2.5 py-1 rounded-full text-xs font-bold animate-fadeIn">
                              Already Assigned
                            </span>
                          )} */}
                        </div>

                        {/* Arrow Icon */}
                        <div className="text-[#06513D]/60 text-3xl font-light">
                          <HiOutlineArrowRight />
                        </div>

                        {/* User Dropdown */}
                        <div className="flex-1">
                          <AntSelect
                            placeholder="Users Picklist"
                            className="w-full h-[48px] custom-mapping-select"
                            value={mapping[itemId] || undefined}
                            onChange={(val) => handleUserMapping(itemId, val)}
                            showSearch
                            optionFilterProp="children"
                            disabled={alreadyAssignedIds.includes(itemId)}
                          >
                            {linkedUsers.map((user) => (
                              <Option key={user._id} value={user._id}>
                                {user.name}
                              </Option>
                            ))}
                            {linkedUsers.length === 0 && (
                              <Option disabled value="none">
                                No linked users found
                              </Option>
                            )}
                          </AntSelect>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Save Button */}
                <div className=" pt-20 flex justify-center gap-6 bg-white">
                  <button
                    type="button"
                    onClick={() => (onClose ? onClose() : navigate(-1))}
                    className="authorize-back-btn"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="authorize-save-btn"
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
                {/* Pagination Footer */}
                <div className="authorize-pagination">
                  <div className="authorize-pagination-left">
                    <span className="text-xs text-gray-600">Showing</span>
                    <select
                      className="authorize-page-size"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      <option value={4}>4</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  <div className="authorize-pagination-center">
                    Showing {(currentPage - 1) * pageSize + 1} to{" "}
                    {Math.min(currentPage * pageSize, filteredItems.length)} out
                    of {filteredItems.length} records
                  </div>
                  <div className="authorize-pagination-right">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredItems.length}
                      onChange={(page) => setCurrentPage(page)}
                      showSizeChanger={false}
                      className="custom-premium-pagination"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full flex justify-center py-10 bg-white">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{ height: 40 }}
                  description={
                    <span className="text-xs text-gray-500 font-medium">
                      No matches found for your search.
                    </span>
                  }
                  style={{ padding: "16px 0" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Popup */}
        {showPopup && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeInFast">
            <PopupCard
              type={popupData.type}
              title={popupData.title}
              message={popupData.message}
              // onConfirm={() => setShowPopup(false)}
            />
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeInFast">
            <div className="bg-white rounded-[32px] w-[90%] max-w-[650px] overflow-hidden shadow-2xl animate-scaleUp relative py-6 px-8">
              {/* Close Button */}
              <button
                onClick={() => setShowConfirmModal(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-100 rounded-full"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Top Checkmark / Confetti */}
              <div className="flex justify-center mb-1 mt-2">
                <img
                  src={firsticon}
                  className="w-20 h-auto object-contain z-10"
                  alt="Success"
                />
              </div>

              {/* Title & Subtitle */}
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">All Set!</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-[380px] mx-auto leading-relaxed">
                  You are about to create a{" "}
                  {isLocationFirst ? "Local" : "Department"} Supervisor for the
                  <br />
                  following {isLocationFirst ? "locations" : "departments"} and
                  user.
                </p>
              </div>

              {/* Selected Counts Cards */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-3 border border-[#e2e8f0] bg-[#f6faf9] rounded-2xl p-2.5 shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-[#EBF5F2] flex items-center justify-center shrink-0">
                    <img
                      src={Locationicon}
                      className="w-4 h-4 object-contain"
                      alt="Location"
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 font-bold tracking-wide">
                      {isLocationFirst ? "Locations" : "Departments"} Selected
                    </div>
                    <div className="text-lg font-bold text-gray-800">
                      {selectedItemIds.length}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 border border-[#e2e8f0] bg-[#f6faf9] rounded-2xl p-2.5 shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-[#EBF5F2] flex items-center justify-center shrink-0">
                    <img
                      src={Usericon}
                      className="w-4 h-4 object-contain"
                      alt="User"
                    />
                  </div>
                  <div>
                    <div className="text-[11px] text-gray-400 font-bold tracking-wide">
                      User Selected
                    </div>
                    <div className="text-lg font-bold text-gray-800">
                      {uniqueUsersCount}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table / List Container */}
              <div className="border border-[#e2e8f0] rounded-2xl overflow-hidden mb-4 bg-white shadow-sm">
                <div className="bg-white px-5 py-2 border-b border-[#f1f5f9]">
                  <h4 className="text-xs font-bold text-[#06513D]">
                    {isLocationFirst
                      ? "Locations & Users"
                      : "Departments & Users"}
                  </h4>
                </div>

                {/* Table Headers */}
                <div className="grid grid-cols-[50px_1fr_1fr] bg-[#f8fafc] px-5 py-1.5 text-[10px] font-semibold text-gray-500 tracking-wide">
                  <div>#</div>
                  <div>
                    {isLocationFirst ? "Location Name" : "Department Name"}
                  </div>
                  <div>Local Supervisor (User)</div>
                </div>

                {/* Scrollable List Body */}
                <div className="max-h-[120px] overflow-y-auto divide-y divide-[#f1f5f9] custom-scrollbar">
                  {selectedPairs.map((pair, index) => (
                    <div
                      key={pair.id}
                      className="grid grid-cols-[50px_1fr_1fr] px-5 py-2.5 items-center text-xs text-gray-700"
                    >
                      <div className="font-semibold text-gray-400">
                        {index + 1}
                      </div>
                      <div className="flex items-center gap-2 font-medium">
                        <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="truncate">{pair.itemName}</span>
                      </div>
                      <div className="flex items-center gap-2 font-semibold text-gray-800">
                        <User className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="truncate">{pair.userName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Informational Banner */}
              <div className="bg-[#eff9f3] border border-[#D1EDE5] rounded-2xl p-3 flex items-center gap-3 mb-4 text-left">
                <div className="w-8 h-8 rounded-lg bg-[#EBF5F2] text-[#06513D] flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4.5 h-4.5" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="text-[10px] font-semibold text-[#06513D] leading-tight m-0 p-0">
                    The selected user will be assigned as{" "}
                    {isLocationFirst ? "Local" : "Department"} Supervisor for
                    the above {isLocationFirst ? "locations" : "departments"}.
                  </p>
                  <p className="text-[10px] font-semibold text-[#06513D] leading-tight m-0 p-0">
                    They will be able to manage data collection and users for
                    their assigned{" "}
                    {isLocationFirst ? "locations" : "departments"}.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-6 mt-4">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="w-[180px] py-2.5 rounded-xl border border-[#06513D]/30 text-[#06513D] font-bold text-[13.5px] hover:bg-[#EBF5F2]/50 transition-all flex items-center justify-center gap-2"
                >
                  ← Back
                </button>
                <button
                  onClick={proceedToSave}
                  disabled={loading}
                  className="w-[220px] py-2.5 rounded-xl bg-[#06513D] text-white font-bold text-[13.5px] hover:bg-[#054031] transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <img
                    src={confirmicon}
                    className="w-4.5 h-4.5 object-contain"
                    alt="Confirm"
                  />
                  {loading ? "Processing..." : "Confirm & Create"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Missing Organization Structure Warning Popup */}
        {showOrgStructureWarning && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeInFast px-4">
            <PopupCard
              type="warning"
              title="Organization Structure Not Found"
              message={`No Location or Department has been added to your organization.\n\nAdd at least one Location and one Department to continue with form assignment and authorization.`}
              confirmText="Add Organization Structure"
              cancelText="Close"
              onConfirm={() => {
                setShowOrgStructureWarning(false);
                navigate("/organization/organization-setup");
              }}
              onCancel={() => setShowOrgStructureWarning(false)}
              widthClass="w-full sm:w-[450px]"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorizeToManage;
